import React from "react";
import { DownloadSimple, Clock } from "@phosphor-icons/react";

/** Riga di lista per il dropdown "Cosa c'è dentro lo .zip" */
function ContentRow({ icon, label, desc }) {
  return (
    <div className="flex items-baseline gap-2 text-[10.5px]">
      <span className="text-[12px]" aria-hidden>{icon}</span>
      <span className="text-white/90 font-medium">{label}</span>
      <span className="text-white/40">— {desc}</span>
    </div>
  );
}

/**
 * PackBox · "Pacchetto Social completo" — sezione del SocialStudioTab.
 *
 * Genera in 1 click 9 file (3 post + 2 prima/dopo + 3 didascalie + README)
 * pronti da pubblicare. L'agente sceglie il tono della didascalia AI e
 * quante varianti (A/B test).
 *
 * Modalità di esecuzione:
 *   • Sincrona (default)  → barra di progress + scarica appena pronto
 *     (richiede di restare sulla pagina ~25-50s)
 *   • In background (NEW) → l'agente lancia il job, può navigare via,
 *     riceve toast quando è pronto e scarica dal tray flottante in basso.
 */
export default function PackBox({
  packBusy,
  captionTone,
  setCaptionTone,
  captionVariants,
  setCaptionVariants,
  onDownloadPack,
  onScheduleBackground,
}) {
  return (
    <div
      className="mb-4 rounded-lg border border-[#1C1C1A] overflow-hidden bg-gradient-to-br from-[#1C1C1A] to-[#2D2D29]"
      data-testid="social-pack-box"
    >
      {/* Top: titolo + descrizione + dropdown "Cosa c'è dentro?" */}
      <div className="px-4 pt-3.5 pb-3 text-white">
        <div className="text-[13.5px] font-semibold flex items-center gap-1.5 mb-0.5">
          Pacchetto Social completo
          <span className="text-[8.5px] font-bold tracking-[0.18em] uppercase text-[#1C1C1A] bg-[#B5C4A6] px-1.5 py-0.5 rounded-sm leading-none">Scorciatoia</span>
        </div>
        <div className="text-[11.5px] text-white/70 leading-snug">
          Premi una volta · ricevi <strong className="text-white">tutti i materiali social</strong> (post, story, carosello, reel, prima/dopo) <strong className="text-white">+ il testo pronto da pubblicare</strong>. Tutto in 1 zip.
        </div>

        {/* Cosa contiene — espandibile */}
        <details className="mt-2.5 group" data-testid="pack-contents-details">
          <summary className="cursor-pointer text-[10.5px] text-[#B5C4A6] hover:text-white/90 list-none flex items-center gap-1 select-none">
            <span className="inline-block transition-transform group-open:rotate-90">▸</span>
            Apri lo zip · cosa trovi dentro
          </summary>
          <div className="mt-2 pl-3 border-l-2 border-[#B5C4A6]/40 space-y-1.5">
            <ContentRow icon="🟦" label="post-1x1.png" desc="1080×1080 · feed Instagram/Facebook" />
            <ContentRow icon="📲" label="post-4x5.png" desc="1080×1350 · feed IG (più spazio)" />
            <ContentRow icon="🖥️" label="post-16x9.png" desc="1920×1080 · Facebook bacheca / LinkedIn" />
            <ContentRow icon="📱" label="story-9x16.png" desc="1080×1920 · Story IG/Facebook · WhatsApp Status" />
            <ContentRow icon="🎠" label="carosello/01..05.png" desc="5 slide · carosello Instagram multi-foto" />
            <ContentRow icon="🎬" label="reel-9x16.mp4" desc="Reel cinematografico 9:16 · ~17s" />
            <ContentRow icon="🎞️" label="prima-dopo-9x16.mp4" desc="Prima/Dopo · Reel/Story 9:16" />
            <ContentRow icon="✏️" label="didascalia.txt" desc="Testo pronto da incollare nel post" />
          </div>
        </details>
      </div>

      {/* iter v28 r38 — Selettore dei 4 toni RIMOSSO da qui per evitare
          duplicazione con la card "Testo del post" sulla destra (l'agente
          vedeva 2 volte gli stessi 4 toni e si confondeva). Qui mostriamo
          SOLO il tono attualmente attivo come riepilogo, e il selettore
          vero (con anteprima dei testi) resta nel CaptionGenerator. */}
      <div className="px-4 pb-3 pt-1 border-t border-white/10">
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-white/55 mb-1.5">
          Tono del testo nello zip
        </div>
        <div className="flex items-center gap-2 text-[12px] text-white/85">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#B5C4A6] text-[#1C1C1A] font-semibold capitalize" data-testid="pack-tone-current">
            {captionTone || "editoriale"}
          </span>
          <span className="text-white/40 text-[11px]">
            Lo cambi nella card &quot;Testo del post&quot; sulla destra.
          </span>
        </div>
      </div>

      {/* Action buttons: 2 modalità affiancate */}
      <div className="grid grid-cols-[1fr_auto] border-t border-white/10">
        <button
          onClick={onDownloadPack}
          disabled={packBusy}
          data-haptic="medium"
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#B5C4A6] hover:bg-[#A0B493] text-[#1C1C1A] text-[13px] font-semibold transition-colors disabled:opacity-60"
          data-testid="social-pack-download"
          title="Genera il pacchetto subito (~30s). Resta sulla pagina fino al download."
        >
          {packBusy ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-[#1C1C1A] border-t-transparent rounded-full animate-spin" />
              Genero il pacchetto…
            </>
          ) : (
            <>
              <DownloadSimple size={16} weight="bold" /> Scarica subito
            </>
          )}
        </button>
        <button
          onClick={onScheduleBackground}
          disabled={packBusy}
          className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white text-[12px] font-medium border-l border-white/10 transition-colors disabled:opacity-50"
          data-testid="social-pack-schedule"
          title="Lancia il job in background — puoi cambiare schermata, ti avvisiamo a fine elaborazione."
        >
          <Clock size={14} weight="bold" />
          Lascialo lavorare
        </button>
      </div>
      {/* iter 261: micro-spiegazione delle 2 modalità */}
      <div className="px-4 py-2 bg-white/5 text-[10.5px] text-white/55 leading-snug">
        <strong className="text-white/75">Scarica subito</strong>: aspetti ~30s e scarichi. <strong className="text-white/75">Lascialo lavorare</strong>: torni alla home, ti avvisiamo quando è pronto.
      </div>
    </div>
  );
}
